import GameConfig from "../core/data/GameConfig";
import Strings from "../core/data/Strings";
import GameScene from "../scenes/GameScene";
import Fonts from "../utils/Fonts";
import GameColors from "../utils/GameColors";
import { AirInput } from "./AirInput";
import CharacterAnimator from "./CharacterAnimator";
import { Flag } from "./Flag";
import { GameObject } from "./GameObject";
import { Goal } from "./Goal";
import { GroundInput } from "./GroundInput";
import { Obstacle } from "./Obstacle";
import { PlayerMovement } from "./PlayerMovement";
import { PointsIndicator } from "./PointsIndicator";
import { Ramp } from "./Ramp";
import { Star } from "./Star";

export class Player extends GameObject {
	animator: CharacterAnimator;
	movement: PlayerMovement;

	airInput: AirInput;
	shadow: Phaser.GameObjects.Container;

	flurry: Phaser.GameObjects.Image;

	snowVelocity: { min: number; max: number };
	emitters: Phaser.GameObjects.Particles.ParticleEmitter[];

	inputObject: Phaser.GameObjects.Image;
	invuln = 0;
	public readonly startX = 400;

	trickDirection: string;
	bubbleActive: boolean;
	public static readonly ON_LAUNCH = "onLaunch";
	public static readonly ON_FLAG_COLLECT = "onFlagCollect";

	scene: GameScene;
	pointIndicators: PointsIndicator[] = [];
	cursors: Phaser.Types.Input.Keyboard.CursorKeys;

	topLane: number;
	bottomLane: number;

	onRamp: boolean;

	constructor(scene: GameScene, topLane: number, bottomLane: number, inputObj: Phaser.GameObjects.Image) {
		super(scene);
		this.scene = scene;
		this.bubbleActive = false;
		this.x = this.startX;
		this.y = topLane;
		this.topLane = topLane;
		this.bottomLane = bottomLane;
		this.onRamp = false;
		const flurry = (this.flurry = this.addImage("skiFlurry"));
		flurry.setPosition(-90, -75);

		this.shadow = scene.add.container(0, 0);
		this.add(this.shadow);
		const shadowImg = this.addImage("shadow");
		shadowImg.setOrigin(0.5);
		shadowImg.setPosition(-80, -35);
		this.shadow.add(shadowImg);
		this.shadow.setSize(shadowImg.displayWidth, shadowImg.displayHeight);

		scene.add.tween({
			targets: this.shadow,
			scaleY: 0.85,
			angle: 5,
			duration: 400,
			yoyo: true,
			repeat: -1,
		});

		this.emitters = [];
		//snow particles
		const particles = ["skiSnow1", "skiSnow2", "skiSnow3", "skiSparkle1", "skiSparkle2"];
		for (let i = 0; i < particles.length; i++) {
			const particle = scene.add.particles(particles[i]);
			particle.setPosition(-80, -10);
			this.add(particle);

			const snow = i < 3;
			const emitter = particle.createEmitter({ frequency: snow ? 80 : 200 });
			this.emitters.push(emitter);
			emitter.setPosition(this.position, this.position - 20);
			const scale = snow ? { min: 0.3, max: 1 } : { min: 0.7, max: 1.5 };
			emitter.setScale(scale);
			emitter.setGravity(50, 100);
			emitter.setLifespan({ min: 300, max: 500 });
			emitter.setAlpha({ start: 1, end: 0, ease: "Quad.easeIn" });
			emitter.setAngle({ min: 0, max: 359 });

			emitter.onParticleEmit(this.onParticleSpawn, this);
			emitter.tint.onUpdate = this.onParticleUpdate;
		}

		const animator = (this.animator = this.addComponent(new CharacterAnimator(this, { key: "player" })));
		animator.spineContainer.setAngle(-scene.angle);
		this.setSize(animator.spine.displayWidth, animator.spine.displayHeight);
		this.playIdle();

		scene.physics.world.enableBody(this, Phaser.Physics.Arcade.DYNAMIC_BODY);
		const body = this.body as Phaser.Physics.Arcade.StaticBody;
		body.setSize(100, 60, false);
		const dx = -(body.position.x - this.x);
		const dy = -(body.position.y - this.y);
		body.setOffset(dx - body.width / 2, dy - body.height);

		this.movement = this.addComponent(new PlayerMovement(this, topLane, bottomLane));
		this.getComponent(PlayerMovement).events.once(
			PlayerMovement.ON_HIT_GROUND,
			() => {
				this.createBubble(false);
			},
			this
		);
		this.cursors = this.scene.input.keyboard.createCursorKeys();

		this.inputObject = inputObj;
		this.setGroundInput();
		scene.input.keyboard.on(
			"keydown-SPACE",
			() => {
				this.keyPressed("keydown-SPACE");
			},
			this
		);
	}

	private onParticleSpawn(particle: Phaser.GameObjects.Particles.Particle): void {
		particle.x -= Phaser.Math.RND.realInRange(0, 50);
		particle.y -= Phaser.Math.RND.realInRange(0, 30);
		const speedMod = this.movement.speed / this.movement.maxSpeed;
		const maxY = 400 * speedMod + 100;
		particle.velocityY = Phaser.Math.RND.realInRange(-maxY, -50);
		const xSpeed = { max: speedMod * 500 + 100, min: speedMod * 250 + 50 };
		particle.velocityX = Phaser.Math.RND.realInRange(-xSpeed.max, -xSpeed.min);
		(<any>particle).scale = particle.scaleX;
		(<any>particle).rotVel = Phaser.Math.RND.realInRange(0.2, 1);
	}

	private onParticleUpdate(particle: Phaser.GameObjects.Particles.Particle): number {
		if (particle.lifeCurrent > 100) {
			const scale = (<any>particle).scale;
			const progress = Phaser.Math.Clamp((particle.lifeCurrent - 100) / (particle.life - 100), 0, 1);
			particle.scaleX = scale * progress;
			particle.scaleY = scale * progress;
		}

		const rotVel = (<any>particle).rotVel as number;
		particle.rotation = rotVel * (particle.lifeCurrent / 1000);
		return 0xffffff;
	}

	private fadeFluff(fadeIn: boolean, delay = 0): void {
		this.scene.tweens.killTweensOf(this.flurry);
		if (fadeIn) {
			this.scene.add.tween({
				targets: this.flurry,
				alpha: 0.6,
				duration: 400,
				delay: delay,
			});
		} else {
			this.scene.add.tween({
				targets: this.flurry,
				alpha: 0,
				duration: 400,
				delay: delay,
			});
		}
	}

	playIdle(): void {
		this.animator.playIdle("Idle_cruising", 3, ["push-off-with-poles"]);
	}

	keyPressed(event: string): void {
		if (event == "keydown-SPACE") {
			if (this.movement.onGround) this.movement.swapLanes();
		}
	}

	/**
	 * Starts playing the player's idle animation(s) after the current animation finishes
	 */
	playIdleNext(): void {
		this.animator.spine.once("complete", this.playIdle, this);
	}

	update(dt: number): void {
		if (this.invuln > 0) {
			// this.alpha = 0.6;
			this.invuln -= dt;
		} else this.alpha = 1;

		if (!this.onGround) {
			const shadowDist = this.animator.spine.y;
			this.shadow.alpha = Phaser.Math.Clamp(1 + shadowDist / 400, 0, 1);
			this.shadow.scale = Phaser.Math.Clamp(1 + shadowDist / 1200, 0, 1);
		}
		if (this.onGround) {
			if (this.cursors.up.isDown) {
				this.movement.swapLanes(this.topLane);
			} else if (this.cursors.down.isDown) {
				this.movement.swapLanes(this.bottomLane);
			}
			if (this.scene.tutorial != undefined && this.scene.tutorial.isTutorial1) {
				if (this.cursors.down.isDown || this.cursors.up.isDown) this.scene.tutorial.dismissHand();
			}
		}
		super.update(dt);
	}

	onOverlap(us: Player, them: GameObject): void {
		if (this.onGround) {
			if (them instanceof Obstacle && this.invuln <= 0) {
				this.invuln = 1;
				this.getComponent(PlayerMovement).bump();
				this.createBubble(true);
				this.scene.addScore(GameConfig.get("scoreObstacle"), true);
				this.scene.soundManager.play("hitObstacleSFX");
			}
			if (them instanceof Flag && them.enabled) {
				them.onHit();
				this.emit(Player.ON_FLAG_COLLECT);
				this.gainPoints(GameConfig.get("scoreFlag"), false);
				//this.scene.soundManager.play("flagCollectSFX");
			}
			if (them instanceof Star && them.enabled) {
				this.gainPoints(GameConfig.get("scoreStar"), false);
				//this.scene.soundManager.play("flagCollectSFX");
				them.onHit();
			}
			if (them instanceof Ramp) {
				if (!this.onRamp) {
					this.onRamp = true;
					this.fadeFluff(false);
					this.trickDirection = them.currentDirection;
					// console.log("Trick Direction: " + this.trickDirection);
					this.movement.enableLaneSwitch = false;
					them.showArrow();
					this.setAirInput();
					this.emitters.forEach((e) => e.stop());
					this.scene.soundManager.play("rampSFX");

					this.scene.time.delayedCall(200, () => {
						this.movement.jump();
						this.emit(Player.ON_LAUNCH);
					});
				}
			}
			if (them instanceof Goal && them.enabled) {
				them.onHit();
				this.scene.soundManager.play("levelCompleteSFX");
				this.scene.onFinish();
			}
		}
	}

	get onGround(): boolean {
		return this.movement.onGround;
	}

	createBubble(playerHit: boolean): void {
		if (this.bubbleActive) return;
		this.bubbleActive = true;
		const bubbleContainer = this.scene.add.container(0, 0);
		this.add(bubbleContainer);

		const imgWidth = playerHit ? 200 : 250;
		const bubbleBG = this.scene.add.nineSlicedSprite(
			0,
			0,
			playerHit ? "badBubble" : "goodBubble",
			imgWidth,
			175,
			23,
			23,
			23,
			23,
			null,
			false
		);
		this.add(bubbleBG);
		bubbleContainer.add(bubbleBG);
		bubbleBG.setPosition(0, -400);

		bubbleBG.setPosition(0, -400);

		bubbleBG.setScale(0);
		const text = this.scene.add.text(0, 0, playerHit ? Strings.get("Ouch") : Strings.get("Woohoo"), {
			color: playerHit ? GameColors.DARKPINK : GameColors.BLUE,
			fontFamily: Fonts.DisneyJrBold,
			fontSize: "30px",
		});
		text.setOrigin(0.5);
		text.setPosition(0, -415);
		this.add(text);
		text.setScale(0);
		bubbleContainer.add(text);
		bubbleContainer.setSize(bubbleBG.displayWidth, bubbleBG.displayHeight);

		this.scene.add.tween({
			targets: [bubbleBG, text],
			scale: { from: 0, to: 1 },
			alpha: { from: 0, to: 1 },
			duration: 500,
			delay: 0,
			hold: 800,
			yoyo: true,
			ease: Phaser.Math.Easing.Back.In,
			onComplete: () => {
				bubbleContainer.destroy();
				bubbleBG.destroy();
				text.destroy();
				this.bubbleActive = false;
			},
		});
	}

	setGroundInput(): void {
		this.fadeFluff(true);
		this.emitters.forEach((e) => e.start());

		const airInput = (this.airInput = this.getComponent(AirInput));
		if (airInput) this.removeComponent(airInput);
		if (!this.getComponent(GroundInput)) {
			this.addComponent(new GroundInput(this, this.inputObject));
		}
	}

	setAirInput(): void {
		const groundInput = this.getComponent(GroundInput);
		if (groundInput) this.removeComponent(groundInput);
		if (!this.getComponent(AirInput)) {
			this.addComponent(new AirInput(this, this.inputObject));
		}
	}

	gainPoints(points: number, ramp: boolean): void {
		const pts = new PointsIndicator(this.scene, this.x, this.y - 50, points, ramp);
		this.pointIndicators.push(pts);
		pts.angle = -this.scene.angle;
		this.scene.addObject(pts, "foreground");
		this.scene.addScore(points);
	}

	onPause(): void {
		this.animator.pause();
		this.emitters.forEach((e) => e.pause());
		for (const indicator of this.pointIndicators) {
			PointsIndicator.pauseEmitters(indicator.emitters);
		}
	}

	onResume(): void {
		this.animator.resume();
		this.emitters.forEach((e) => e.resume());
		for (const indicator of this.pointIndicators) {
			PointsIndicator.resumeEmitters(indicator.emitters);
		}
	}
}
